<!--
  EmailTypePicker.vue -- the "Editing email" chooser dropdown (PR-4).

  Lists all catalog email types; each row shows icon + label + one-line
  description + a Customized / Default badge. Data-driven from the catalog
  (passed in) and the ``customizedKeys`` set (which per-type rows exist).

  a11y: a real menu button (aria-haspopup) toggling a keyboard-navigable
  option list; each option is a labeled button.
-->
<template>
  <div class="email-type-picker" data-testid="email-type-picker">
    <button
      type="button"
      class="email-type-picker__trigger"
      data-testid="email-type-picker-trigger"
      aria-haspopup="listbox"
      :aria-expanded="open ? 'true' : 'false'"
      @click="open = !open"
    >
      <v-icon aria-hidden="true">{{ current.icon }}</v-icon>
      <span>{{ current.label }}</span>
      <v-icon class="email-type-picker__caret" aria-hidden="true"
        >expand_more</v-icon
      >
    </button>

    <template v-if="open">
      <div class="email-type-picker__scrim" @mousedown="open = false" />
      <div
        class="email-type-picker__menu"
        role="listbox"
        aria-label="Transactional emails"
      >
        <div class="email-type-picker__menu-head">
          <div class="email-type-picker__menu-eyebrow">
            Transactional emails
          </div>
          <div class="email-type-picker__menu-sub">
            Each type has its own copy and its own insertable fields. Sender
            identity and header image are shared.
          </div>
        </div>
        <button
          v-for="t in emailTypes"
          :key="t.key"
          type="button"
          class="email-type-picker__option"
          :class="{ 'email-type-picker__option--active': t.key === modelValue }"
          role="option"
          :aria-selected="t.key === modelValue ? 'true' : 'false'"
          :data-testid="`email-type-option-${t.key}`"
          @mousedown.prevent="select(t.key)"
        >
          <v-icon class="email-type-picker__option-icon" aria-hidden="true">{{
            t.icon
          }}</v-icon>
          <span class="email-type-picker__option-body">
            <span class="email-type-picker__option-head">
              <span class="email-type-picker__option-label">{{
                t.label
              }}</span>
              <span
                class="email-type-picker__badge"
                :class="{
                  'email-type-picker__badge--customized': isCustomized(t.key),
                }"
                :data-testid="`email-type-badge-${t.key}`"
                >{{ isCustomized(t.key) ? "Customized" : "Default copy" }}</span
              >
            </span>
            <span class="email-type-picker__option-desc">{{
              t.description
            }}</span>
          </span>
        </button>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: "EmailTypePicker",

  props: {
    // Active email-type key (v-model).
    modelValue: { type: String, required: true },
    // Catalog entries: [{ key, label, icon, description, ... }].
    emailTypes: { type: Array, required: true },
    // Set/array of type keys that have a stored (customized) row.
    customizedKeys: { type: Array, default: () => [] },
  },

  emits: ["update:modelValue"],

  data() {
    return { open: false };
  },

  computed: {
    current() {
      return (
        this.emailTypes.find((t) => t.key === this.modelValue) ||
        this.emailTypes[0] ||
        { key: "", label: "", icon: "mail" }
      );
    },
    customizedSet() {
      return new Set(this.customizedKeys || []);
    },
  },

  methods: {
    isCustomized(key) {
      return this.customizedSet.has(key);
    },
    select(key) {
      this.open = false;
      if (key !== this.modelValue) {
        this.$emit("update:modelValue", key);
      }
    },
  },
};
</script>

<style scoped>
.email-type-picker {
  position: relative;
  display: inline-block;
}
.email-type-picker__trigger {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 11px 6px 13px;
  border: none;
  border-radius: 9999px;
  background: var(--tf-primary-soft, #e3f2fd);
  color: var(--tf-primary, #119ae9);
  font-size: 13.5px;
  font-weight: 500;
  cursor: pointer;
}
.email-type-picker__caret {
  font-size: 18px;
  opacity: 0.7;
}
.email-type-picker__scrim {
  position: fixed;
  inset: 0;
  z-index: 30;
}
.email-type-picker__menu {
  position: absolute;
  top: 40px;
  left: 0;
  z-index: 40;
  width: 340px;
  background: #fff;
  border: 1px solid var(--tf-border, #cfd8e3);
  border-radius: 12px;
  box-shadow: 0 11px 15px -7px rgba(0, 0, 0, 0.2),
    0 24px 38px 3px rgba(0, 0, 0, 0.14);
  overflow: hidden;
}
.email-type-picker__menu-head {
  padding: 10px 14px 8px;
  border-bottom: 1px solid var(--tf-border, #cfd8e3);
}
.email-type-picker__menu-eyebrow {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: var(--tf-fg-4, #78909c);
}
.email-type-picker__menu-sub {
  font-size: 12px;
  color: var(--tf-fg-4, #78909c);
  margin-top: 3px;
  line-height: 1.4;
}
.email-type-picker__option {
  display: flex;
  align-items: flex-start;
  gap: 11px;
  width: 100%;
  text-align: left;
  padding: 11px 14px;
  border: none;
  border-bottom: 1px solid #f0f2f5;
  background: #fff;
  cursor: pointer;
}
.email-type-picker__option:hover {
  background: #f3f8fd;
}
.email-type-picker__option--active {
  background: var(--tf-primary-soft, #e3f2fd);
}
.email-type-picker__option-icon {
  font-size: 20px;
  color: var(--tf-fg-4, #78909c);
  margin-top: 1px;
}
.email-type-picker__option--active .email-type-picker__option-icon {
  color: var(--tf-primary, #119ae9);
}
.email-type-picker__option-body {
  min-width: 0;
  flex: 1;
}
.email-type-picker__option-head {
  display: flex;
  align-items: center;
  gap: 7px;
}
.email-type-picker__option-label {
  font-size: 13.5px;
  font-weight: 500;
  color: var(--tf-fg-1, #133752);
}
.email-type-picker__option--active .email-type-picker__option-label {
  color: var(--tf-primary, #119ae9);
}
.email-type-picker__badge {
  margin-left: auto;
  font-size: 9.5px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--tf-fg-4, #78909c);
  background: #f0f2f5;
  border-radius: 9999px;
  padding: 2px 8px;
}
.email-type-picker__badge--customized {
  color: var(--tf-primary, #119ae9);
  background: var(--tf-primary-soft, #e3f2fd);
}
.email-type-picker__option-desc {
  display: block;
  font-size: 11.5px;
  color: var(--tf-fg-4, #78909c);
  margin-top: 2px;
  line-height: 1.45;
}
</style>
